// ============================================================
// Motor de cálculo de riesgo ergonómico — Empuje, Arrastre y
// Transporte (Snook y Ciriello, percentil 90)
//
// Fuente: INSST (Instituto Nacional de Seguridad y Salud en el
// Trabajo, España), calculadora oficial "Manipulación manual: empuje,
// arrastre y transporte" (2020, NIPO 118-20-079-6),
// https://herramientasprl.insst.es/ergonomia/manipulacion-manual-empuje-arrastre-transporte
// — implementa los valores de Snook, S.H. y Ciriello, V.M. (1991)
// "The design of manual handling tasks: revised tables of maximum
// acceptable weights and forces" (Ergonomics, 34(9), 1197-1213) para
// el percentil 90 de la población. Datos extraídos de los endpoints
// JSON públicos de esa calculadora (assets/data/*.json) el 14/09/2026,
// verificados contra la respuesta real del servidor.
//
// Alcance confirmado por el usuario: solo Transporte, Empuje y
// Arrastre — Levantamiento/Descenso quedan cubiertos por NIOSH
// (ver src/lib/niosh.ts), ya que el propio INSST recomienda NIOSH
// para esas tareas. Solo percentil 90 — resultado binario
// (aceptable/no aceptable), sin el nivel intermedio "mejorable" del
// método académico completo: esta fuente no expone los otros 4
// percentiles.
//
// Motor de cálculo puro, sin UI:
//   - Tablas completas de Transporte (masculino/femenino) y de
//     Empuje/Arrastre (masculino/femenino), copiadas byte a byte de
//     los JSON originales del INSST — no modificar sin repetir la
//     verificación contra el servidor.
//   - Lookup directo (sin interpolación): la calculadora del INSST
//     solo ofrece opciones discretas de distancia/altura/frecuencia,
//     así que este motor replica ese mismo comportamiento.
//   - Evaluación contra el umbral del 90% con las correcciones del
//     método general (agarre deficiente, carga alejada del cuerpo).
// ============================================================

export type SnookTipoTarea = 'transporte' | 'empuje' | 'arrastre';
export type SnookSexo = 'masculino' | 'femenino';

interface BloqueDistanciaTransporte {
  distancia: string; // "2,1" | "4,3" | "8,5"
  frecuencias: string[]; // etiquetas, ej. "6 s", "1 min", "8 h" — mismo orden que los arrays de valores
  alturas: { altura: string; valores: number[] }[]; // valores paralelos a frecuencias
}

interface BloqueDistanciaEmpujeArrastre {
  distancia: string;
  frecuencias: string[];
  alturas: { altura: string; inicial: number[]; sostenida: number[] }[]; // paralelos a frecuencias
}

// ===== Tablas — Transporte =====

const TRANSPORTE_MASCULINO: BloqueDistanciaTransporte[] = [
  { distancia: "2,1", frecuencias: ["6 s","12 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "111", valores: [10,14,17,17,19,21,25] },
    { altura: "79", valores: [13,17,21,21,23,26,31] } ] },
  { distancia: "4,3", frecuencias: ["10 s","16 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "111", valores: [9,11,15,15,17,19,22] },
    { altura: "79", valores: [11,14,18,19,21,23,27] } ] },
  { distancia: "8,5", frecuencias: ["18 s","24 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "111", valores: [10,11,13,13,15,17,20] },
    { altura: "79", valores: [13,18,17,18,20,22,26] } ] }
];

const TRANSPORTE_FEMENINO: BloqueDistanciaTransporte[] = [
  { distancia: "2,1", frecuencias: ["6 s","12 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "105", valores: [11,12,13,13,13,13,18] },
    { altura: "72", valores: [13,14,16,16,16,16,22] } ] },
  { distancia: "4,3", frecuencias: ["10 s","16 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "105", valores: [9,10,13,13,13,13,18] },
    { altura: "72", valores: [10,11,14,14,14,14,20] } ] },
  { distancia: "8,5", frecuencias: ["18 s","24 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "105", valores: [10,11,12,12,12,12,16] },
    { altura: "72", valores: [12,12,14,14,14,14,19] } ] }
];

// ===== Tablas — Empuje =====

const EMPUJE_MASCULINO: BloqueDistanciaEmpujeArrastre[] = [
  { distancia: "2,1", frecuencias: ["6 s","12 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [20,22,25,25,26,26,31], sostenida: [10,13,15,16,18,18,22] },
    { altura: "95", inicial: [21,24,26,26,28,28,34], sostenida: [10,13,16,17,19,19,23] },
    { altura: "64", inicial: [19,22,24,24,25,26,31], sostenida: [10,13,16,16,18,19,23] } ] },
  { distancia: "7,6", frecuencias: ["15 s","22 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [14,16,21,21,22,22,26], sostenida: [8,9,13,13,15,16,18] },
    { altura: "95", inicial: [16,18,23,23,25,25,30], sostenida: [8,10,13,13,15,15,18] },
    { altura: "64", inicial: [13,14,20,20,21,21,26], sostenida: [8,10,12,13,14,15,18] } ] },
  { distancia: "15,2", frecuencias: ["25 s","35 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [16,18,19,19,20,21,25], sostenida: [8,9,11,12,13,14,16] },
    { altura: "95", inicial: [18,21,22,22,23,24,28], sostenida: [8,10,11,12,13,13,16] },
    { altura: "64", inicial: [15,17,19,19,20,20,24], sostenida: [8,10,11,11,12,13,15] } ] },
  { distancia: "30,5", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [15,16,19,19,24], sostenida: [8,10,12,13,16] },
    { altura: "95", inicial: [17,19,22,22,27], sostenida: [8,10,12,13,16] },
    { altura: "64", inicial: [14,16,19,19,23], sostenida: [8,9,11,13,15] } ] },
  { distancia: "45,7", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [13,14,16,16,20], sostenida: [7,8,10,11,13] },
    { altura: "95", inicial: [14,16,19,19,23], sostenida: [7,8,9,11,13] },
    { altura: "64", inicial: [12,14,16,16,20], sostenida: [7,8,9,11,13] } ] },
  { distancia: "61,0", frecuencias: ["2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [12,14,14,18], sostenida: [7,8,9,11] },
    { altura: "95", inicial: [14,16,16,20], sostenida: [7,8,9,11] },
    { altura: "64", inicial: [12,14,14,17], sostenida: [7,8,9,10] } ] }
];

const EMPUJE_FEMENINO: BloqueDistanciaEmpujeArrastre[] = [
  { distancia: "2,1", frecuencias: ["6 s","12 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [14,15,17,18,20,21,22], sostenida: [6,8,10,10,11,12,14] },
    { altura: "89", inicial: [14,15,17,18,20,21,22], sostenida: [6,7,9,9,10,11,13] },
    { altura: "57", inicial: [11,12,14,14,16,17,18], sostenida: [5,6,8,8,9,9,12] } ] },
  { distancia: "7,6", frecuencias: ["15 s","22 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [15,16,16,16,18,19,20], sostenida: [6,7,7,7,8,9,11] },
    { altura: "89", inicial: [14,15,16,17,19,19,21], sostenida: [6,7,8,8,9,9,11] },
    { altura: "57", inicial: [11,12,14,14,16,16,17], sostenida: [6,7,7,7,8,9,11] } ] },
  { distancia: "15,2", frecuencias: ["25 s","35 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [12,14,14,14,15,16,17], sostenida: [5,6,6,6,7,7,9] },
    { altura: "89", inicial: [11,13,14,14,16,16,17], sostenida: [5,6,6,7,7,8,10] },
    { altura: "57", inicial: [9,11,12,12,13,14,15], sostenida: [5,6,6,6,7,7,9] } ] },
  { distancia: "30,5", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [12,13,14,15,17], sostenida: [5,6,6,6,8] },
    { altura: "89", inicial: [12,14,15,16,18], sostenida: [5,6,6,7,9] },
    { altura: "57", inicial: [11,12,12,13,15], sostenida: [5,6,6,6,8] } ] },
  { distancia: "45,7", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [12,13,14,15,17], sostenida: [5,5,5,6,8] },
    { altura: "89", inicial: [12,14,15,16,18], sostenida: [5,6,6,6,8] },
    { altura: "57", inicial: [11,12,12,13,15], sostenida: [5,5,5,6,7] } ] },
  { distancia: "61,0", frecuencias: ["2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [12,13,14,15], sostenida: [4,4,4,6] },
    { altura: "89", inicial: [12,13,14,16], sostenida: [4,4,5,6] },
    { altura: "57", inicial: [10,11,12,13], sostenida: [4,4,4,6] } ] }
];

// ===== Tablas — Arrastre =====

const ARRASTRE_MASCULINO: BloqueDistanciaEmpujeArrastre[] = [
  { distancia: "2,1", frecuencias: ["6 s","12 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [14,16,18,18,19,19,23], sostenida: [8,10,12,13,15,15,18] },
    { altura: "95", inicial: [19,22,25,25,27,27,32], sostenida: [10,13,16,17,19,20,24] },
    { altura: "64", inicial: [22,25,28,28,30,30,36], sostenida: [11,14,17,18,20,21,25] } ] },
  { distancia: "7,6", frecuencias: ["15 s","22 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [11,13,16,16,17,18,21], sostenida: [6,8,10,11,12,12,15] },
    { altura: "95", inicial: [15,18,23,23,24,24,29], sostenida: [8,10,13,14,16,16,19] },
    { altura: "64", inicial: [18,20,26,26,27,28,33], sostenida: [9,11,14,15,17,17,20] } ] },
  { distancia: "15,2", frecuencias: ["25 s","35 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [13,15,15,15,16,17,20], sostenida: [7,8,9,9,10,11,13] },
    { altura: "95", inicial: [18,20,21,21,23,23,28], sostenida: [9,10,12,12,14,14,17] },
    { altura: "64", inicial: [20,23,24,24,26,26,31], sostenida: [9,11,12,13,15,15,18] } ] },
  { distancia: "30,5", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [12,13,15,15,19], sostenida: [7,8,9,11,13] },
    { altura: "95", inicial: [16,18,21,21,26], sostenida: [9,10,12,14,17] },
    { altura: "64", inicial: [18,21,24,24,30], sostenida: [9,11,13,15,18] } ] },
  { distancia: "45,7", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [10,11,13,13,16], sostenida: [6,7,8,9,10] },
    { altura: "95", inicial: [14,16,18,18,23], sostenida: [7,9,10,12,14] },
    { altura: "64", inicial: [16,18,21,21,26], sostenida: [8,9,11,12,15] } ] },
  { distancia: "61,0", frecuencias: ["2 min","5 min","30 min","8 h"], alturas: [
    { altura: "144", inicial: [10,11,11,14], sostenida: [6,6,7,9] },
    { altura: "95", inicial: [13,16,16,19], sostenida: [7,9,10,12] },
    { altura: "64", inicial: [15,18,18,22], sostenida: [8,9,10,12] } ] }
];

const ARRASTRE_FEMENINO: BloqueDistanciaEmpujeArrastre[] = [
  { distancia: "2,1", frecuencias: ["6 s","12 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [13,16,17,18,20,21,22], sostenida: [6,9,10,10,11,12,15] },
    { altura: "89", inicial: [14,16,18,19,21,22,23], sostenida: [6,9,10,10,11,12,14] },
    { altura: "57", inicial: [15,17,19,20,22,23,24], sostenida: [5,8,9,9,10,11,13] } ] },
  { distancia: "7,6", frecuencias: ["15 s","22 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [13,14,16,16,18,19,20], sostenida: [7,8,9,9,10,11,13] },
    { altura: "89", inicial: [14,15,16,17,19,20,21], sostenida: [7,8,9,9,10,10,13] },
    { altura: "57", inicial: [15,16,17,18,20,21,22], sostenida: [6,7,8,8,9,10,12] } ] },
  { distancia: "15,2", frecuencias: ["25 s","35 s","1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [10,12,13,14,15,16,17], sostenida: [6,7,7,8,8,9,11] },
    { altura: "89", inicial: [10,12,14,14,16,17,18], sostenida: [5,6,7,7,8,9,11] },
    { altura: "57", inicial: [11,13,15,15,17,18,19], sostenida: [5,6,7,7,7,8,10] } ] },
  { distancia: "30,5", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [12,13,14,15,17], sostenida: [6,7,7,8,10] },
    { altura: "89", inicial: [13,14,15,16,18], sostenida: [6,7,7,7,10] },
    { altura: "57", inicial: [13,14,15,17,19], sostenida: [6,6,6,7,9] } ] },
  { distancia: "45,7", frecuencias: ["1 min","2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [12,13,14,15,17], sostenida: [6,6,7,7,9] },
    { altura: "89", inicial: [13,14,15,16,18], sostenida: [5,6,6,7,9] },
    { altura: "57", inicial: [13,14,15,17,19], sostenida: [5,6,6,6,8] } ] },
  { distancia: "61,0", frecuencias: ["2 min","5 min","30 min","8 h"], alturas: [
    { altura: "135", inicial: [12,13,14,15], sostenida: [5,5,5,7] },
    { altura: "89", inicial: [12,13,14,16], sostenida: [5,5,5,7] },
    { altura: "57", inicial: [13,14,15,17], sostenida: [4,5,5,6] } ] }
];

// ===== Lookup directo (sin interpolación) =====
// La calculadora del INSST solo ofrece opciones discretas de
// distancia/altura/frecuencia — este motor replica ese mismo
// comportamiento: si la combinación no existe en la tabla, no se
// asume ni se interpola, se devuelve null.

function obtenerBloqueTransporte(sexo: SnookSexo, distancia: string): BloqueDistanciaTransporte | undefined {
  const tabla = sexo === 'masculino' ? TRANSPORTE_MASCULINO : TRANSPORTE_FEMENINO;
  return tabla.find(b => b.distancia === distancia);
}

function obtenerBloqueEmpujeArrastre(
  tipo: 'empuje' | 'arrastre',
  sexo: SnookSexo,
  distancia: string
): BloqueDistanciaEmpujeArrastre | undefined {
  const tabla = tipo === 'empuje'
    ? (sexo === 'masculino' ? EMPUJE_MASCULINO : EMPUJE_FEMENINO)
    : (sexo === 'masculino' ? ARRASTRE_MASCULINO : ARRASTRE_FEMENINO);
  return tabla.find(b => b.distancia === distancia);
}

// ===== Consulta de opciones disponibles (para poblar selects del
// formulario dinámicamente, sin duplicar las tablas) =====

export function obtenerDistanciasDisponibles(tipoTarea: SnookTipoTarea, sexo: SnookSexo): string[] {
  if (tipoTarea === 'transporte') {
    const tabla = sexo === 'masculino' ? TRANSPORTE_MASCULINO : TRANSPORTE_FEMENINO;
    return tabla.map(b => b.distancia);
  }
  const tabla = tipoTarea === 'empuje'
    ? (sexo === 'masculino' ? EMPUJE_MASCULINO : EMPUJE_FEMENINO)
    : (sexo === 'masculino' ? ARRASTRE_MASCULINO : ARRASTRE_FEMENINO);
  return tabla.map(b => b.distancia);
}

export function obtenerAlturasDisponibles(tipoTarea: SnookTipoTarea, sexo: SnookSexo, distancia: string): string[] {
  if (tipoTarea === 'transporte') {
    const bloque = obtenerBloqueTransporte(sexo, distancia);
    return bloque ? bloque.alturas.map(a => a.altura) : [];
  }
  const bloque = obtenerBloqueEmpujeArrastre(tipoTarea, sexo, distancia);
  return bloque ? bloque.alturas.map(a => a.altura) : [];
}

export function obtenerFrecuenciasDisponibles(tipoTarea: SnookTipoTarea, sexo: SnookSexo, distancia: string): string[] {
  if (tipoTarea === 'transporte') {
    const bloque = obtenerBloqueTransporte(sexo, distancia);
    return bloque ? bloque.frecuencias : [];
  }
  const bloque = obtenerBloqueEmpujeArrastre(tipoTarea, sexo, distancia);
  return bloque ? bloque.frecuencias : [];
}

export function consultarPesoMaximoTransporte(
  sexo: SnookSexo,
  distancia: string,
  altura: string,
  frecuencia: string
): number | null {
  const bloque = obtenerBloqueTransporte(sexo, distancia);
  if (!bloque) return null;
  const fila = bloque.alturas.find(a => a.altura === altura);
  if (!fila) return null;
  const indice = bloque.frecuencias.indexOf(frecuencia);
  if (indice === -1) return null;
  return fila.valores[indice] ?? null;
}

export function consultarFuerzasEmpujeArrastre(
  tipo: 'empuje' | 'arrastre',
  sexo: SnookSexo,
  distancia: string,
  altura: string,
  frecuencia: string
): { inicial: number; sostenida: number } | null {
  const bloque = obtenerBloqueEmpujeArrastre(tipo, sexo, distancia);
  if (!bloque) return null;
  const fila = bloque.alturas.find(a => a.altura === altura);
  if (!fila) return null;
  const indice = bloque.frecuencias.indexOf(frecuencia);
  if (indice === -1) return null;
  const inicial = fila.inicial[indice];
  const sostenida = fila.sostenida[indice];
  if (inicial === undefined || sostenida === undefined) return null;
  return { inicial, sostenida };
}

// ===== Evaluación (percentil 90, resultado binario) =====
// Correcciones del método general (Snook, S.H. & Ciriello, V.M. (1991).
// The design of manual handling tasks: revised tables of maximum
// acceptable weights and forces. Ergonomics, 34(9), 1197-1213),
// aplicadas como reducción porcentual sobre el valor tabulado ANTES de
// comparar con el valor real: carga sin asas o con agarre deficiente
// -15%, carga manejada alejada del cuerpo -50%. Ambas se aplican de
// forma multiplicativa.

function aplicarCorrecciones(
  valor: number,
  sinAsasOAgarreDeficiente?: boolean,
  cargaAlejadaDelCuerpo?: boolean
): number {
  let corregido = valor;
  if (sinAsasOAgarreDeficiente) corregido *= 0.85;
  if (cargaAlejadaDelCuerpo) corregido *= 0.5;
  return corregido;
}

export interface ResultadoSnookTransporte {
  maximoAceptable: number;
  pesoReal: number;
  aceptable: boolean; // pesoReal <= maximoAceptable
}

export function evaluarTransporte(
  sexo: SnookSexo,
  distancia: string,
  altura: string,
  frecuencia: string,
  pesoReal: number,
  sinAsasOAgarreDeficiente?: boolean,
  cargaAlejadaDelCuerpo?: boolean
): ResultadoSnookTransporte | null {
  const valorTabulado = consultarPesoMaximoTransporte(sexo, distancia, altura, frecuencia);
  if (valorTabulado === null) return null;
  const maximoAceptable = aplicarCorrecciones(valorTabulado, sinAsasOAgarreDeficiente, cargaAlejadaDelCuerpo);
  return {
    maximoAceptable,
    pesoReal,
    aceptable: pesoReal <= maximoAceptable,
  };
}

export interface ResultadoSnookEmpujeArrastre {
  inicialMaxima: number;
  sostenidaMaxima: number;
  fuerzaInicialReal: number;
  fuerzaSostenidaReal: number;
  aceptableInicial: boolean;
  aceptableSostenida: boolean;
  aceptable: boolean; // aceptableInicial && aceptableSostenida
}

export function evaluarEmpujeArrastre(
  tipo: 'empuje' | 'arrastre',
  sexo: SnookSexo,
  distancia: string,
  altura: string,
  frecuencia: string,
  fuerzaInicialReal: number,
  fuerzaSostenidaReal: number,
  sinAsasOAgarreDeficiente?: boolean,
  cargaAlejadaDelCuerpo?: boolean
): ResultadoSnookEmpujeArrastre | null {
  const valoresTabulados = consultarFuerzasEmpujeArrastre(tipo, sexo, distancia, altura, frecuencia);
  if (valoresTabulados === null) return null;

  const inicialMaxima = aplicarCorrecciones(valoresTabulados.inicial, sinAsasOAgarreDeficiente, cargaAlejadaDelCuerpo);
  const sostenidaMaxima = aplicarCorrecciones(valoresTabulados.sostenida, sinAsasOAgarreDeficiente, cargaAlejadaDelCuerpo);

  const aceptableInicial = fuerzaInicialReal <= inicialMaxima;
  const aceptableSostenida = fuerzaSostenidaReal <= sostenidaMaxima;

  return {
    inicialMaxima,
    sostenidaMaxima,
    fuerzaInicialReal,
    fuerzaSostenidaReal,
    aceptableInicial,
    aceptableSostenida,
    aceptable: aceptableInicial && aceptableSostenida,
  };
}

// ============================================================
// Verificación manual — casos de referencia
// ============================================================
// Caso 1 — Transporte (ejemplo de la propia fuente INSST): hombre,
// altura 111cm, distancia 2,1m, frecuencia "12 s".
//
// const bloque = TRANSPORTE_MASCULINO.find(b => b.distancia === '2,1');
// // frecuencias = ["6 s","12 s","1 min","2 min","5 min","30 min","8 h"]
// // índice de "12 s" = 1
// const fila = bloque.alturas.find(a => a.altura === '111');
// // valores = [10,14,17,17,19,21,25] → valores[1] = 14
//
// const resultado = evaluarTransporte('masculino', '2,1', '111', '12 s', 14);
// // maximoAceptable = 14 (sin correcciones), pesoReal = 14 → aceptable = true
//
// Resultado esperado (confirmado a mano antes de guardar este
// archivo): consultarPesoMaximoTransporte('masculino','2,1','111','12 s') = 14,
// coincide exactamente con el valor publicado por el INSST para este caso.
//
// ------------------------------------------------------------
// Caso 2 — Empuje, con correcciones aplicadas: hombre, altura 144cm,
// distancia 2,1m, frecuencia "6 s", carga sin asas/agarre deficiente
// y manejada alejada del cuerpo.
//
// const bloque = EMPUJE_MASCULINO.find(b => b.distancia === '2,1');
// const fila = bloque.alturas.find(a => a.altura === '144');
// // inicial[0] = 20, sostenida[0] = 10 (frecuencia "6 s" → índice 0)
//
// inicialMaxima = 20 * 0.85 (sin asas) * 0.5 (alejada) = 8.5
// sostenidaMaxima = 10 * 0.85 * 0.5 = 4.25
//
// const resultado = evaluarEmpujeArrastre(
//   'empuje', 'masculino', '2,1', '144', '6 s', 8, 4, true, true
// );
// // aceptableInicial: 8 <= 8.5 → true
// // aceptableSostenida: 4 <= 4.25 → true
// // aceptable: true
//
// Resultado esperado (confirmado a mano antes de guardar este
// archivo): inicialMaxima=8.5, sostenidaMaxima=4.25, aceptable=true —
// con fuerzas reales de 8kgf/4kgf, justo por debajo de los límites
// corregidos por agarre deficiente y carga alejada del cuerpo.
// ============================================================
